import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Popover,
  Typography,
  Checkbox,
  Chip,
  Divider,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useState, useEffect } from "react";
import "./review.scss";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Label from "../../../components/Label/Label";
import Buttons from "../../../components/Controls/Buttons/Button";
import { useDispatch, useSelector } from "react-redux";
import * as types from "../../../redux/review/types";
import { getUsername } from "../../../constants/utility";
import DatePicker from "react-datepicker";
import { format } from "date-fns";

export default function ParameterTable({
  data,
  onSaveRow,
  batchData,
  selectedReviewCheck,
  handleReviwCheckBox,
  selectedSheetCheck,
  updateData,
}) {
  const [menuState, setMenuState] = useState({ anchorEl: null, rowId: null });
  const [openDialog, setOpenDialog] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [errors, setErrors] = useState({});
  const nonEditableColumns = ["Batch ID"];
  const [anchorEl, setAnchorEl] = useState(null);
  const [remarkText, setRemarkText] = useState("");
  const dispatch = useDispatch();

  const handleEyeClick = (event, remark) => {
    setAnchorEl(event.currentTarget);
    setRemarkText(remark);
  };
  const handleEyeClose = () => {
    setAnchorEl(null);
    setRemarkText("");
  };

  const handleRowCheckbox = (sheet_row_id) => {
    handleReviwCheckBox(sheet_row_id);
  };

  const handleHeaderCheckbox = (checked, sheet_id) => {
    if (checked) {
      const ids = data.rows.map((row) => row.sheet_row_id);
      handleReviwCheckBox(ids, sheet_id, true);
    } else {
      const ids = data.rows.map((row) => row.sheet_row_id);
      handleReviwCheckBox(ids, sheet_id, false);
    }
  };

  const handleClick = (event, row) => {
    setMenuState({ anchorEl: event.currentTarget, rowId: row.sheet_row_id });
  };
  const handleCloseMenu = () => setMenuState({ anchorEl: null, rowId: null });
  const { product, site, unitOfOperation, selectedChart, selectedTab } =
    useSelector((state) => state.shared?.productSitesUopValues || {});

  const handleEdit = (row) => {
    const editableValues = {};
    row.values.forEach((val) => {
      if (!nonEditableColumns.includes(val.column_name)) {
        editableValues[`${row.sheet_row_id}-${val.column_id}`] =
          val.value_text || "";
      }
    });
    setEditValues(editableValues);
    setErrors({});
    setEditRow(row);
    setOpenDialog(true);
    handleCloseMenu();
  };

  const validateField = (val, value) => {
    let rules = [];
    if (val.validation_rule) {
      try {
        const parsed = JSON.parse(val.validation_rule);
        rules = parsed.rules || [];
      } catch (e) {
        console.error("Invalid validation_rule JSON:", val.validation_rule);
      }
    }
    if (val.is_required && (!value || value.trim() === "")) {
      return `${val.column_name} is required`;
    }
    for (const rule of rules) {
      if (rule.type === "date") {
        const datePattern = /^(0[1-9]|[12][0-9]|3[01])-[A-Z][a-z]{2}-\d{4}$/;
        if (!datePattern.test(value)) {
          return rule.error_message || `${val.column_name} must be in dd-MMM-yyyy format`;
        }
      }
      // if (rule.required && (!value || value.trim() === "")) {
      //   return rule.error_message || `${val.column_name} is required`;
      // }

      if (rule.type === "numeric" && value && isNaN(Number(value))) {
        return rule.error_message || `${val.column_name} must be numeric`;
      }

      if (rule.type === "text" && value && !/^[a-zA-Z]+$/.test(value)) {
        return rule.error_message || `${val.column_name} must be text`;
      }




      // Add more rule types as needed (min, max, regex, etc.)
    }

    return null; // no error
  };

  const handleSubmit = () => {
    const newErrors = {};
    let isValid = true;
    let batchDate = null; // 👈 store modified date

    editRow.values.forEach((val) => {
      const fieldKey = `${editRow.sheet_row_id}-${val.column_id}`;
      const value =
        editValues[fieldKey] !== undefined
          ? editValues[fieldKey]
          : val.value_text || "";

      let error = validateField(val, value);

      if (error) {
        isValid = false;
        newErrors[fieldKey] = error;
      }

      // 👇 check if date changed
      if (
        val.data_type === "date" &&
        editValues[fieldKey] !== undefined &&
        editValues[fieldKey] !== val.value_text
      ) {
        batchDate = editValues[fieldKey];
      }
    });

    if (!editValues.update_remark || editValues.update_remark.trim() === "") {
      isValid = false;
      newErrors.update_remark = "Change Reason is required";
    }

    if (!isValid) {
      setErrors(newErrors);
      return;
    }

    const payload = {
      batch_id: batchData.batch_id,
      row_id: editRow.sheet_row_id,
      update_remark: editValues.update_remark,
      unit_op_id: unitOfOperation?.id,
      updated_by: getUsername(),

      // 👇 add batch_date only if modified
      ...(batchDate && { batch_date: batchDate }),

      values: editRow.values
        .filter((val) => !nonEditableColumns.includes(val.column_name))
        .map((val) => {
          const fieldKey = `${editRow.sheet_row_id}-${val.column_id}`;
          const newValue = editValues[fieldKey];
          const oldValue = val.value_text;

          if (newValue !== undefined && newValue !== oldValue) {
            return {
              value_id: val.value_id,
              value_text: newValue,
            };
          }
          return null;
        })
        .filter(Boolean),
    };

    onSaveRow(payload);
  };

  useEffect(() => {
    if (updateData?.status === 200) {
      setOpenDialog(false);
      setEditRow(null);
    }
  }, [updateData]);

  return (
    <Box width="100%">
      {/* Table */}
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: "calc(100vh - 230px)",
          boxShadow: "none",
          overflow: "auto",
          width: "calc(100vw - 360px)",
        }}
      >
        <Table stickyHeader sx={{ tableLayout: "auto" }}>
          <TableHead className="table-header">
            <TableRow>
              <TableCell className="table-header-cell">
                <Checkbox
                  checked={selectedSheetCheck?.includes(data?.sheet_id)}
                  onChange={(e) =>
                    handleHeaderCheckbox(e.target.checked, data?.sheet_id)
                  }
                  size="small"
                />
              </TableCell>
              {data?.rows?.[0]?.values?.map((val) => (
                <TableCell className="table-header-cell" key={val.column_id}>
                  {val.column_name}
                </TableCell>
              ))}
              <TableCell
                className="table-header-cell"
                sx={{ paddingLeft: "40px !important" }}
              >
                Status
              </TableCell>
              <TableCell
                className="table-header-cell"
                sx={{
                  paddingLeft: "10px !important ",
                  textAlign: "center",
                  maxWidth: "120px",
                }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <Divider sx={{ border: "none", height: "2px" }} />
          <TableBody
            sx={{
              "& .MuiTableRow-root": { height: 48 },
              "& .MuiTableCell-root": { py: 0.5 },
            }}
          >
            {data?.rows?.map((row) => (
              <TableRow
                key={row.sheet_row_id}
                sx={{
                  backgroundColor:
                    editRow?.sheet_row_id === row?.sheet_row_id
                      ? "#D5D7DA"
                      : "",
                }}
              >
                <TableCell sx={{ paddingLeft: "20px !important" }}>
                  {row.status !== "Approved" ? (
                    <>
                      <Checkbox
                        checked={selectedReviewCheck?.includes(
                          row.sheet_row_id
                        )}
                        onChange={() => handleRowCheckbox(row.sheet_row_id)}
                        size="small"
                      />
                    </>
                  ) : (
                    ""
                  )}
                </TableCell>
                {row?.values?.map((val) => (
                  <TableCell sx={{ fontSize: 14 }} key={val.value_id}>
                    {val.value_text ?? "-"}
                  </TableCell>
                ))}
                <TableCell>
                  {" "}
                  <Chip
                    label={row.status}
                    className={`review-status-chip status-chip ${row.status === "Approved" ? "status-chip-color-green" : "status-chip-color-gray"}`}
                  />
                </TableCell>
                <TableCell sx={{ minWidth: "120px", textAlign: "center" }}>
                  <IconButton
                    disabled={!row?.is_editable}
                    onClick={(e) => handleClick(e, row)}
                  >
                    <MoreVertIcon />
                  </IconButton>
                  <Menu
                    anchorEl={menuState.anchorEl}
                    open={menuState.rowId === row.sheet_row_id}
                    onClose={handleCloseMenu}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    MenuListProps={{
                      sx: {
                        padding: 0,
                        backgroundColor: "white",
                        borderRadius: 0,
                      },
                    }}
                  >
                    <MenuItem onClick={() => handleEdit(row)}>Edit</MenuItem>
                  </Menu>
                  {row.update_remark && (
                    <IconButton
                      onClick={(e) => handleEyeClick(e, row.update_remark)}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!data?.loader && data?.rows?.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={data?.rows?.[0]?.values?.length + 3 || 7}
                  align="center"
                >
                  <Label color="textSecondary" text={"No data found"} />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Dialog */}
      <Dialog
        PaperProps={{
          sx: {
            margin: 0,
            width: 450,
            minHeight: 550,
            overflow: "auto",
            position: "fixed",
            bottom: 0,
            right: 0,
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            p: 0.5,
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "transparent",
            backdropFilter: "none",
          },
        }}
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
          // dispatch({ type: types.CLEAR_UPDATE_MESSAGE });
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit</DialogTitle>
        <DialogContent dividers>
          {editRow?.values?.map((val) => {
            const fieldKey = `${editRow.sheet_row_id}-${val.column_id}`;
            const isEditable = !nonEditableColumns.includes(val.column_name);
            const value = editValues[fieldKey] ?? val.value_text;
            if (val.data_type === "date") {
              return (
                <Box sx={{ width: "100%" }}>
                  <DatePicker
                    key={fieldKey}
                    selected={value ? new Date(value) : null}
                    showYearDropdown
                    scrollableYearDropdown
                    wrapperClassName="w-full"
                    yearDropdownItemNumber={150}
                    dateFormat="dd-MMM-yyyy"
                    onChange={(date) =>
                      isEditable &&
                      setEditValues((prev) => ({
                        ...prev,
                        [fieldKey]: date ? format(date, "dd-MMM-yyyy") : "",
                      }))
                    }
                    customInput={
                      <TextField
                        fullWidth
                        size="small"
                        margin="dense"
                        label={val.is_required ? `${val.column_name} *` : val.column_name}
                        error={!!errors[fieldKey]}
                        helperText={errors[fieldKey] || ""}
                        disabled={!isEditable}
                        InputLabelProps={{ shrink: true }}
                        style={{ width: "100%" }}
                      />
                    }
                  /></Box>
              );
            }
            return (
              <TextField
                key={fieldKey}
                fullWidth
                size="small"
                margin="dense"
                label={
                  val.is_required
                    ? `${val.column_name} *`
                    : val.column_name
                }
                value={editValues[fieldKey] ?? val.value_text}
                onChange={(e) =>
                  isEditable &&
                  setEditValues((prev) => ({
                    ...prev,
                    [fieldKey]: e.target.value,
                  }))
                }
                error={!!errors[fieldKey]}
                helperText={errors[fieldKey] || ""}
                disabled={!isEditable}
              />
            );
          })}

          {/* Change Reason */}
          <TextField
            fullWidth
            size="small"
            margin="dense"
            label="Change Reason"
            value={editValues.update_remark || ""}
            onChange={(e) =>
              setEditValues((prev) => ({
                ...prev,
                update_remark: e.target.value,
              }))
            }
            error={!!errors.update_remark}
            helperText={errors.update_remark || ""}
            required
          />
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setOpenDialog(false);
              setEditRow(null);
            }}
          >
            Cancel
          </Button>
          <Buttons
            loading={updateData?.loader}
            onClick={handleSubmit}
            text={"Save"}
            variant="contained"
          ></Buttons>
        </DialogActions>
      </Dialog>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleEyeClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: { p: 1.5, maxWidth: 250, borderRadius: 2, boxShadow: 3 },
        }}
      >
        <Typography variant="body2">{remarkText}</Typography>
      </Popover>
    </Box>
  );
}
